/**
 * Edit Order Header — Change Goldsmith (cancel-and-recreate).
 *
 * Changing the goldsmith mints a BRAND-NEW order form under a fresh order
 * number (the number embeds the new goldsmith's code) and PERMANENTLY DELETES
 * the current order. This is required because the order number encodes the
 * goldsmith prefix — an in-place rename would leave a misleading number. It
 * calls ``POST /api/orders/{id}/change-karigar`` with ``delete_old_order``.
 *
 * Rules (mirrored + re-enforced by the backend):
 *   - Requires the ``orders.edit_header`` permission (implied by
 *     ``orders.edit`` and by Company Admin / Super Admin).
 *   - Blocked on Completed / Cancelled orders.
 *   - Blocked (and hidden by the caller) the moment ANY raw material —
 *     gold or a goldsmith-issue line — has been issued on the order, so
 *     the ledgers stay coherent.
 *
 * The caller is responsible for hiding the affordance when material has
 * been issued; the backend re-checks and returns a 409 otherwise.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { Hammer, Loader2 } from 'lucide-react';

export function ChangeGoldsmithDialog({
  open,
  onOpenChange,
  order,
  companyId,          // legacy prop — retained for compatibility
  onSaved,            // (updatedOrder) => void
}) {
  const [goldsmiths, setGoldsmiths] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Prefer the order's own company_id — always present and correct even
  // when the caller couldn't pass a company scope (e.g. a Super Admin who
  // opened the order via a deep URL before picking a company).
  const scopedCompanyId = order?.company_id || companyId;

  // Reset state on open.
  useEffect(() => {
    if (open) {
      setSelectedId('');
      setSubmitting(false);
    }
  }, [open]);

  // Load goldsmiths scoped to the order's company.
  useEffect(() => {
    if (!open || !scopedCompanyId) return;
    setLoading(true);
    api
      .get('/masters/goldsmiths', { params: { company_id: scopedCompanyId } })
      .then(({ data }) => setGoldsmiths(Array.isArray(data) ? data : []))
      .catch((e) => toast.error(extractError(e, 'Failed to load goldsmiths')))
      .finally(() => setLoading(false));
  }, [open, scopedCompanyId]);

  // Everything except the currently-assigned goldsmith + archived rows.
  const availableGoldsmiths = useMemo(() => {
    const currentId = order?.goldsmith_id || '';
    return goldsmiths.filter((g) => !g.is_archived && g.id !== currentId);
  }, [goldsmiths, order?.goldsmith_id]);

  const submit = async () => {
    if (submitting) return;
    if (!selectedId) {
      toast.error('Please pick a goldsmith to switch to.');
      return;
    }
    if (selectedId === (order?.goldsmith_id || '')) {
      onOpenChange(false);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/change-karigar`, {
        new_goldsmith_id: selectedId,
        delete_old_order: true,
      });
      const newOrder = data?.new_order || null;
      toast.success(
        `New order ${newOrder?.order_no || ''} created for the new goldsmith; ${order.order_no} removed.`,
      );
      onOpenChange(false);
      if (onSaved) onSaved(newOrder);
    } catch (e) {
      toast.error(extractError(e, 'Failed to change goldsmith'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="change-goldsmith-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hammer size={18} className="text-[var(--ink-navy)]" /> Change Goldsmith
          </DialogTitle>
          <DialogDescription>
            Reassign order&nbsp;
            <span className="font-mono font-medium text-[var(--ink-navy)]">
              {order?.order_no}
            </span>
            &nbsp;to a different goldsmith. A new order form will be created under
            a fresh order number for the new goldsmith, and this order will be
            permanently removed. Currently assigned to{' '}
            <span className="font-medium text-[var(--ink-navy)]">
              {order?.goldsmith_name || '(none)'}
            </span>
            .
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {/* Current goldsmith (read-only reference) */}
          <div className="rounded-md border border-[var(--divider)] bg-slate-50 p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">
              Current Goldsmith
            </div>
            <div className="text-sm font-medium text-[var(--ink-navy)]">
              {order?.goldsmith_name || '—'}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              New goldsmith <span className="text-rose-500">*</span>
            </Label>
            <SearchableSelect
              items={availableGoldsmiths}
              value={selectedId}
              onValueChange={(v) => setSelectedId(v || '')}
              itemToValue={(g) => g.id}
              itemToLabel={(g) => g.name + (g.phone ? ` · ${g.phone}` : '')}
              searchKeys={['name', 'phone', 'email']}
              placeholder={loading ? 'Loading goldsmiths…' : 'Search goldsmiths by name or phone…'}
              disabled={loading || submitting}
              emptyLabel={
                loading
                  ? 'Loading…'
                  : (goldsmiths.length === 0
                      ? 'No goldsmiths found in this company. Add one from the Goldsmiths master first.'
                      : (availableGoldsmiths.length === 0
                          ? 'No other goldsmiths available to switch to.'
                          : 'No matches for your search.'))
              }
              testId="change-goldsmith-picker"
            />
            <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
              Only goldsmiths from the same company are listed. The currently
              assigned goldsmith is hidden so you cannot pick it again.
              {loading && <Loader2 size={11} className="animate-spin ml-1" />}
            </div>
          </div>

          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 leading-relaxed">
            Changing the goldsmith is only possible before any raw material is
            issued. Once gold or stones have been issued against this order the
            goldsmith can no longer be changed here.
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="change-goldsmith-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !selectedId}
            className="luxury-button"
            data-testid="change-goldsmith-submit"
          >
            {submitting ? (
              <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving…</>
            ) : (
              <><Hammer size={14} className="mr-1.5" /> Save</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ChangeGoldsmithDialog;
